import React, { useRef, useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { DataGrid, GridToolbar, useGridApiRef } from '@mui/x-data-grid';
import { Box, Button, CircularProgress, Popover, FormControlLabel, Checkbox } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { enUS, arSA, he } from 'date-fns/locale'; // Import desired locales
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Tooltip } from '@mui/material';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useFontSize } from '../../Contexts/FontSizeContext';
import Constants from '../../assests/Constants/constants';
dayjs.extend(utc); // Make sure this is declared at the top of your file

const STORAGE_KEY_MAIN_COLUMN_VISIBILITY = 'main_ClientsDataGrid_columnVisibility';
const STORAGE_KEY_MAIN_COLUMN_VISIBILITY_CUSTOM = 'main_ClientsDataGrid_columnVisibility_custom';
const STORAGE_KEY_MAIN_COLUMN_WIDTHS = 'main_ClientsDataGrid_columnWidths';
const STORAGE_KEY_MAIN_COLUMN_WIDTHS_CUSTOM = 'main_ClientsDataGrid_columnWidthsCustom';
const STORAGE_KEY_MAIN_COLUMN_ORDER = 'main_ClientsDataGrid_columnOrder';

const readStoredColumnOrder = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY_MAIN_COLUMN_ORDER);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((field) => typeof field === 'string') : [];
    } catch {
        return [];
    }
};

const CHECKBOX_COL_WIDTH = 50;
const FIT_WIDTH_GUTTER = 24;

const availableColumnWidth = (containerWidth) =>
    Math.max(0, containerWidth - CHECKBOX_COL_WIDTH - FIT_WIDTH_GUTTER);

const isColumnShown = (visibilityModel, field) => visibilityModel?.[field] !== false;

const computeFittedWidths = (columns, visibilityModel, containerWidth, preferredWidths = {}) => {
    const available = availableColumnWidth(containerWidth);
    const visible = columns.filter((col) => isColumnShown(visibilityModel, col.field));
    if (!visible.length || available <= 0) return null;

    const weights = visible.map((col) => {
        const preferred = preferredWidths?.[col.field];
        if (preferred > 0) return preferred;
        return Math.max(1, col.minWidth || 80);
    });
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
    const widths = {};
    let used = 0;

    visible.forEach((col, index) => {
        const isLast = index === visible.length - 1;
        const remaining = available - used;
        const share = isLast
            ? remaining
            : Math.min(remaining, Math.floor((weights[index] / weightSum) * available));
        widths[col.field] = Math.max(0, share);
        used += widths[col.field];
    });

    return widths;
};

const widthsDiffer = (previous, next, columns, visibilityModel) => columns.some((col) => {
    if (!isColumnShown(visibilityModel, col.field)) return false;
    return Math.abs((previous?.[col.field] || 0) - (next?.[col.field] || 0)) > 1;
});

const orderColumns = (columns, order) => {
    if (!order?.length) return columns;
    const byField = new Map(columns.map((col) => [col.field, col]));
    const ordered = [];
    order.forEach((field) => {
        const col = byField.get(field);
        if (!col) return;
        ordered.push(col);
        byField.delete(field);
    });
    byField.forEach((col) => ordered.push(col));
    return ordered;
};
const ROW_HEIGHT_BY_FONT = {
    small: 34,
    medium: 40,
    large: 48,
};

const VIEW_INCLUDES_TIER = {
    compact: ['priority', 'compact'],
    standard: ['priority', 'compact', 'standard'],
    full: ['priority', 'compact', 'standard', 'full'],
    custom: ['priority', 'compact', 'standard', 'full'],
};

const COLUMN_VIEW_TIERS = {
    name: 'priority',
    businessOwnerName: 'compact',
    caseId: 'compact',
    officeIdentifyNumber: 'full',
    caseType: 'compact',
    subAccountantId: 'compact',
    reportingType: 'standard',
    advancedReportingType: 'standard',
    withholdingReporting: 'standard',
    vatReportStatus: 'priority',
    advanceReportStatus: 'priority',
    totalCounts: 'priority',
    processedThisMonth: 'standard',
    unfinishedAssignments: 'priority',
    accountManagement: 'standard',
    clientPaymentMethod: 'full',
    hasCreditCard: 'full',
    isMasavEnabled: 'standard',
    withholdingTaxApprovalValidUntil: 'full',
    registerDate: 'full',
    balance: 'standard',
};

// Shared pill/badge styling so every in-cell badge looks consistent across the grid.
const PILL_BASE = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 10px',
    borderRadius: '999px',
    fontSize: 'var(--xtram-grid-pill-font, 12px)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minHeight: 'var(--xtram-grid-pill-height, 24px)',
    lineHeight: 1,
    boxSizing: 'border-box',
};

const PILL_VARIANTS = {
    success: { bg: 'rgba(34, 197, 94, 0.14)', color: '#15803D', border: 'rgba(34, 197, 94, 0.38)' },
    warning: { bg: 'rgba(245, 158, 11, 0.16)', color: '#B45309', border: 'rgba(245, 158, 11, 0.42)' },
    error: { bg: 'rgba(244, 63, 94, 0.14)', color: '#BE123C', border: 'rgba(244, 63, 94, 0.38)' },
    neutral: { bg: 'rgba(148, 163, 184, 0.16)', color: '#475569', border: 'rgba(148, 163, 184, 0.42)' },
    info: { bg: 'rgba(48, 79, 255, 0.12)', color: '#304FFF', border: 'rgba(48, 79, 255, 0.35)' },
};

const Pill = ({ variant = 'neutral', children, style }) => {
    const c = PILL_VARIANTS[variant] || PILL_VARIANTS.neutral;
    return (
        <span
            style={{
                ...PILL_BASE,
                backgroundColor: c.bg,
                color: c.color,
                border: `1px solid ${c.border}`,
                ...style,
            }}
        >
            {children}
        </span>
    );
};

// Styled DataGrid component — clean, professional white/light admin table.
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    width: '100%',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Montserrat, sans-serif',
    '& *': {
        boxSizing: 'border-box !important',
    },
    // Header — compact height aligned with TransactionsTable (40px).
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#e3e7fe !important',
        borderBottom: '1px solid #E5E7EB',
        minHeight: 'var(--xtram-grid-row-height, 40px) !important',
        maxHeight: 'var(--xtram-grid-row-height, 40px) !important',
        lineHeight: 'var(--xtram-grid-row-height, 40px) !important',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#e3e7fe !important',
        height: 'var(--xtram-grid-row-height, 40px) !important',
        minHeight: 'var(--xtram-grid-row-height, 40px) !important',
        maxHeight: 'var(--xtram-grid-row-height, 40px) !important',
        padding: '0 6px',
    },
    '& .MuiDataGrid-columnHeader:last-of-type': {
        borderInlineEnd: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#1222b3',
        textAlign: 'center',
        fontSize: 'var(--xtram-grid-header-font, 13px)',
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'center',
        height: 'var(--xtram-grid-row-height, 40px)',
        maxHeight: 'var(--xtram-grid-row-height, 40px)',
    },
    '& .MuiDataGrid-columnHeaderDraggableContainer': {
        height: 'var(--xtram-grid-row-height, 40px)',
        maxHeight: 'var(--xtram-grid-row-height, 40px)',
    },
    // Keep sorting arrows visible.
    '& .MuiDataGrid-iconButtonContainer': {
        visibility: 'visible',
        width: 'auto',
    },
    '& .MuiDataGrid-sortIcon': {
        opacity: '1 !important',
        color: '#2532ae',
    },
    '& .MuiDataGrid-columnSeparator': {
        color: '#E5E7EB',
    },
    // Rows — white with thin gray separators and a subtle hover.
    '& .MuiDataGrid-row': {
        cursor: 'pointer',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #EEF1F5',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: '#F8FAFC !important',
    },
    // Keep the leading-business highlight, but make it subtle.
    '& .leading-business-row': {
        backgroundColor: '#FFFBEB !important',
    },
    '& .leading-business-row:hover': {
        backgroundColor: '#FEF3C7 !important',
    },
    // Cells — centered, consistent padding, thin vertical separators, safe wrapping.
    '& .MuiDataGrid-cell': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderInlineEnd: '1px solid #F1F5F9',
        color: '#0f1438',
        fontWeight: 500,
        fontSize: 'var(--xtram-grid-cell-font, 13px)',
    },
    '& .MuiDataGrid-cellContent': {
        fontSize: 'inherit',
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#FFFFFF',
    },
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: '#FFFFFF',
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-scrollbar': {
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    // The horizontal scrollbar container defaults to display: inline-block, which
    // collapses the track width in RTL so it doesn't span the full table. Force it
    // to block so it stretches across the whole grid (same fix used by the other grids).
    '& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--horizontal': {
        display: 'block !important',
    },
    '& .disableUser': {
        pointerEvents: 'none',
        opacity: 0.5,
    },
    '&.fit-to-screen .MuiDataGrid-cell': {
        padding: '0 2px',
        overflow: 'hidden',
    },
    '&.fit-to-screen .MuiDataGrid-columnHeader': {
        padding: '0 2px',
    },
}));

const ClientsDataGrid = ({
    filteredClients,
    viewClientDetails,
    subAccountants,
    activeUsers,
    ViewSubAccountantClients,
    isLoadingDocumentCounts = false,
    onWithholdingReportingToggle,
    withholdingReportingSavingCaseId,
    selectedRowIds = [],
    onSelectedRowIdsChange,
    columnsPanelAnchor = null,
    onColumnsPanelClose,
    tableView = 'standard',
    onTableViewChange,
    fitToScreen = false,
    fitNonce = 0,
}) => {

    const { i18n, t } = useTranslation();
    const { fontSize } = useFontSize();
    const rowHeight = ROW_HEIGHT_BY_FONT[fontSize] ?? ROW_HEIGHT_BY_FONT.medium;
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') || 'he');
    const direction = i18n.dir();
    // const initChatConf = async () => {
    //   await ChatService.InitChat();

    // }

    

    useEffect(() => {
        // Check for saved language in localStorage
        const currentLanguage = localStorage.getItem('language') || 'he'; // Default to 'en' if no language is stored

        i18n.changeLanguage(currentLanguage);
        // initChatConf();
    }, []);

    useEffect(() => {
        const currentLanguage = localStorage.getItem('language') || 'he';
        setSelectedLanguage(currentLanguage);
    }, [i18n.language]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY_MAIN_COLUMN_VISIBILITY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        } catch {
            return {};
        }
    });

    const [columnWidths, setColumnWidths] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY_MAIN_COLUMN_WIDTHS);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        } catch {
            return {};
        }
    });

    const [columnOrder, setColumnOrder] = useState(readStoredColumnOrder);
    const [dragField, setDragField] = useState(null);


    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_VISIBILITY, JSON.stringify(columnVisibilityModel));
            if (tableView === 'custom') {
                localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
            }
        } catch (_) {}
    }, [columnVisibilityModel, tableView]);

    const prevTableViewRef = useRef(tableView);
    const skipCustomLoadRef = useRef(false);

    useEffect(() => {
        const prev = prevTableViewRef.current;
        if (prev === tableView) return;

        if (tableView !== 'custom') {
            setColumnVisibilityModel((current) => {
                try {
                    localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(current));
                } catch (_) {}
                return {};
            });
        } else if (!skipCustomLoadRef.current) {
            try {
                const raw = localStorage.getItem(STORAGE_KEY_MAIN_COLUMN_VISIBILITY_CUSTOM);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (typeof parsed === 'object' && parsed !== null) {
                        setColumnVisibilityModel(parsed);
                    }
                }
            } catch (_) {}
        } else {
            skipCustomLoadRef.current = false;
        }

        prevTableViewRef.current = tableView;
    }, [tableView]);

    const handleColumnVisibilityModelChange = (newModel) => {
        setColumnVisibilityModel(newModel);
        if (tableView !== 'custom') {
            skipCustomLoadRef.current = true;
            onTableViewChange?.('custom');
        }
    };

    const baseColumns = useMemo(() => {
        // Base columns, each with flex: 1 and a minWidth
        const baseColumns = [
            {
                field: 'name',
                headerName: t('clientName'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (

                    <span
                        style={{
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            fontSize: 'inherit',
                        }}
                    >
                        {(params.row.leadingBusiness ? '👑 ' : '') + params.value}
                    </span>
                )
            },
            {
                field: 'businessOwnerName',
                headerName: t('businessOwnerName'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 140,
                valueFormatter: (value) => value ?? '',
                renderCell: (params) => (
                    <span
                        style={{
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            fontSize: 'inherit',
                        }}
                    >
                        {params.value ?? ''}
                    </span>
                ),
            },
            {
                field: 'caseId',
                headerName: t('CompanyId'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'officeIdentifyNumber',
                headerName: t('Number'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 70,
            },
            {
                field: 'caseType',
                headerName: t('caseType'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 110,
            },
            {
                field: 'subAccountantId',
                headerName: t('SubAccountantName'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 160,
                renderCell: (params) => {
                    const subAccountant = subAccountants.find(
                        (accountant) => accountant.value === params.value
                    );
                    return <span>{subAccountant ? subAccountant.label : ''}</span>;
                },
            },
            {
                field: 'reportingType',
                headerName: t('MonthlyBiMonthly'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 110,
                renderCell: (params) => {
                    const caseType = params.row.caseType;
                    return Constants.TaxExemptTypes.includes(caseType) ? '-' : t(params.value);
                },
            },
            {
                field: 'advancedReportingType',
                headerName: t('AdvanceIncomeTaxReportType'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 140,
                renderCell: (params) => {
                    const caseType = params.row.caseType;
                    if (Constants.TaxExemptTypes.includes(caseType)) {
                        return params.value ? t(params.value) : '';
                    }
                    return t(params.value);
                },
            },
            {
                field: 'withholdingReporting',
                headerName: t('WithholdingDeductionReport'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 110,
                renderCell: (params) => {
                    const isSaving = withholdingReportingSavingCaseId === params.row.caseId;
                    const enabled = !!params.value;

                    const segmentStyle = (isSelected) => ({
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 10px',
                        borderRadius: '999px',
                        cursor: isSaving ? 'default' : 'pointer',
                        backgroundColor: isSelected ? '#304FFF' : 'transparent',
                        color: isSelected ? '#FFFFFF' : '#64748B',
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: 'var(--xtram-grid-pill-font, 11px)',
                        transition: 'all 0.2s ease',
                        minWidth: '30px',
                        height: '20px',
                        userSelect: 'none',
                    });

                    const handleSelect = (e, wantEnabled) => {
                        e.stopPropagation();
                        if (isSaving || enabled === wantEnabled) return;
                        onWithholdingReportingToggle?.(params.row);
                    };

                    if (isSaving) {
                        return (
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress size={16} />
                            </div>
                        );
                    }

                    return (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    gap: '2px',
                                    height: '24px',
                                    padding: '2px',
                                    borderRadius: '999px',
                                    background: '#F1F5F9',
                                    border: '1px solid #E2E8F0',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => handleSelect(e, true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSelect(e, true);
                                        }
                                    }}
                                    style={segmentStyle(enabled)}
                                >
                                    {t('Yes')}
                                </div>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => handleSelect(e, false)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSelect(e, false);
                                        }
                                    }}
                                    style={segmentStyle(!enabled)}
                                >
                                    {t('No')}
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            {
                field: 'vatReportStatus',
                headerName: t('VATReport'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 150,
                type: 'singleSelect',
                valueOptions: ['reported', 'notReported', 'notReportedperiod', 'notForReport'],
                valueGetter: (...args) => {
                    // Support both old/new DataGrid valueGetter signatures.
                    const firstArg = args[0];
                    if (firstArg && typeof firstArg === 'object' && 'row' in firstArg) {
                        return firstArg.row?.vatReportStatus || firstArg.value || 'notReported';
                    }
                    const value = args[0];
                    const row = args[1];
                    return row?.vatReportStatus || value || 'notReported';
                },

                renderCell: (params) => {
                    // ManageOnly clients don't run reporting workflows — collapse to a neutral dash.
                    if (params.row?.isManageOnly) {
                        return <div style={{ color: '#9e9e9e' }}>—</div>;
                    }
                    const status = params.value || 'notReported';
                    let className = '';

                    switch (status) {
                        case 'reported':
                            className = 'main-unProcessed'; // Green
                            break;
                        case 'notReported':
                            className = 'main-processed'; // Red
                            break;
                        case 'notReportedperiod':
                            className = 'main-warning'; // Yellow
                            break;
                        case 'notForReport':
                            className = 'main-notforreport';      // New custom style
                            break;
                        default:
                            className = 'main-unProcessed';
                    }

                    return <div className={className}>{t(status)}</div>;
                },
            },
            {
                field: 'advanceReportStatus',
                headerName: t('AdvanceTaxReport'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 150,
                type: 'singleSelect',
                valueOptions: ['reported', 'notReported', 'notReportedperiod', 'notForReport'],
                valueGetter: (...args) => {
                    // Support both old/new DataGrid valueGetter signatures.
                    const firstArg = args[0];
                    if (firstArg && typeof firstArg === 'object' && 'row' in firstArg) {
                        return firstArg.row?.advanceReportStatus || firstArg.value || 'notReported';
                    }
                    const value = args[0];
                    const row = args[1];
                    return row?.advanceReportStatus || value || 'notReported';
                },
                renderCell: (params) => {
                    if (params.row?.isManageOnly) {
                        return <div style={{ color: '#9e9e9e' }}>—</div>;
                    }
                    const status = params.value || 'notReported';
                    let className = '';

                    switch (status) {
                        case 'reported':
                            className = 'main-unProcessed'; // Red
                            break;
                        case 'notReported':
                            className = 'main-processed'; // Green
                            break;
                        case 'notReportedperiod':
                            className = 'main-warning'; // Yellow
                            break;
                        case 'notForReport':
                            className = 'main-notforreport';      // New custom style
                            break;
                        default:
                            className = 'main-unProcessed';
                    }

                    return <div className={className}>{t(status)}</div>;
                },
            },
            {
                field: 'totalCounts',
                headerName: t('unprocessed'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 90,
                renderCell: (params) => {
                    if (params.row?.isManageOnly) {
                        return <div style={{ color: '#9e9e9e' }}>—</div>;
                    }
                    // ONLY show loading indicator if value is null (not fetched yet)
                    // Don't show loading for 0 - that's a valid fetched value
                    if (params.value === null) {
                        return (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                gap: '4px'
                            }}>
                                <CircularProgress size={14} thickness={4} />
                                <span style={{ fontSize: 'var(--xtram-grid-pill-font, 11px)', color: '#666' }}>
                                    {t('loading') || 'טוען...'}
                                </span>
                            </div>
                        );
                    }
                    
                    // Show the actual value (including 0 if that's the real count)
                    return (
                        <div className={`${params.value > 0 ? 'main-processed' : 'main-unProcessed'}`}>
                            {params.value}
                        </div>
                    );
                },
            },
            {
                field: 'processedThisMonth',
                headerName: t('processedThisMonth'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 120,
                type: 'number',
                renderCell: (params) => {
                    if (params.row?.isManageOnly) {
                        return <div style={{ color: '#9e9e9e' }}>—</div>;
                    }
                    // Loading: undefined or null → not fetched yet; 0 is a valid value.
                    if (params.value === null || params.value === undefined) {
                        return (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                            }}>
                                <CircularProgress size={14} thickness={4} />
                                <span style={{ fontSize: 'var(--xtram-grid-pill-font, 11px)', color: '#666' }}>
                                    {t('loading') || 'טוען...'}
                                </span>
                            </div>
                        );
                    }
                    return (
                        <div className={`${params.value > 0 ? 'main-unProcessed' : 'main-processed'}`}>
                            {params.value}
                        </div>
                    );
                },
            },
            {
                field: 'unfinishedAssignments',
                headerName: t('UnfinishedAssignments'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className={`${params.value > 0 ? 'main-assignments' : 'main-assignments-notfinished'}`}>
                            {params.value || 0}
                        </div>
                    </div>
                ),
            },
            {
                field: 'accountManagement',
                headerName: t('AccountManagement'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 130,
            },
            {
                field: 'clientPaymentMethod',
                headerName: t('PaymentMethod'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 130,
                renderCell: (params) => {
                    const val = params.value;
                    const isClient = val === 1 || val === '1' || String(val) === Constants.ClientPaymentMethod?.Client;
                    return (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Pill variant={isClient ? 'info' : 'success'}>
                                {isClient ? t('Client') : t('Accountant')}
                            </Pill>
                        </div>
                    );
                },
            },
            {
                field: 'hasCreditCard',
                headerName: t('CreditCard'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 110,
                renderCell: (params) => {
                    const row = params.row;
                    const paysAsClient = row.clientPaymentMethod === 1 || row.clientPaymentMethod === '1' || String(row.clientPaymentMethod) === Constants.ClientPaymentMethod?.Client;
                    return (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {!paysAsClient ? (
                                <Pill variant="neutral">—</Pill>
                            ) : (
                                <Pill variant={params.value ? 'success' : 'warning'}>
                                    {params.value ? t('Yes') : t('No')}
                                </Pill>
                            )}
                        </div>
                    );
                },
            },
            {
                field: 'isMasavEnabled',
                headerName: t('MasavEnabled'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 100,
                type: 'boolean',
                valueGetter: (...args) => {
                    const firstArg = args[0];
                    if (firstArg && typeof firstArg === 'object' && 'row' in firstArg) {
                        return !!firstArg.row?.isMasavEnabled;
                    }
                    return !!(args[1]?.isMasavEnabled ?? args[0]);
                },
                renderCell: (params) => (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Pill variant={params.value ? 'success' : 'neutral'}>
                            {params.value ? t('Yes') : t('No')}
                        </Pill>
                    </div>
                ),
            },
            {
                field: 'withholdingTaxApprovalValidUntil',
                headerName: t('WithholdingTaxApprovalValidity'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 230,
                renderCell: (params) => {
                    if (params.row?.isManageOnly) {
                        return <span style={{ color: '#9e9e9e' }}>—</span>;
                    }
                    const value = params.value;
                    if (!value) return '';

                    const date = dayjs.utc(value).startOf('day');
                    const today = dayjs.utc().startOf('day');
                    const formatted = date.format('DD/MM/YYYY');

                    const isExpired = date.isBefore(today);
                    const expiresWithinMonth = !isExpired && !date.isAfter(today.add(1, 'month'));
                    const statusLabel = isExpired
                        ? `${t('expired')} ${formatted}`
                        : expiresWithinMonth
                            ? `${t('ExpiresInOneMonth')} ${formatted}`
                            : formatted;
                    const statusColor = isExpired ? '#d32f2f' : expiresWithinMonth ? '#B45309' : '#388e3c';

                    return (
                        <Tooltip title={statusLabel}>
                                <span
                                    style={{
                                        color: statusColor,
                                        fontWeight: 600,
                                        fontSize: 'var(--xtram-grid-cell-font, 13px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        wordBreak: 'break-word',
                                        maxWidth: 260,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={statusLabel}
                                >
                                    {isExpired || expiresWithinMonth ? (
                                        <>
                                            <WarningIcon fontSize="small" />
                                            {statusLabel}
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircleIcon fontSize="small" />
                                            {formatted.length > 15
                                                ? formatted.slice(0, 12) + '...'
                                                : formatted}
                                        </>
                                    )}
                                </span>
                        </Tooltip>
                    );
                }
            },
            {
                field: 'registerDate',
                headerName: t('RegisterDate'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 130,
                renderCell: (params) => {
                    const value = params.value;
                    if (!value) return '';
                    // Accepts either Date/string or timestamp, but expects date-like input
                    const date = dayjs(value);
                    if (!date.isValid()) return value;
                    return date.format('MM/YYYY');
                }
            },

        ];

        // Add the balance column conditionally
        if (!ViewSubAccountantClients) {

            baseColumns.push({
                field: 'balance',
                headerName: t('Balance'),
                viewTier: 'standard',
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                minWidth: 90,
                sortComparator: (v1, v2) => {
                    // Convert beautified numbers back to raw numbers for comparison
                    const parseNumber = (value) => {
                        if (typeof value === 'string') {
                            return parseFloat(
                                value.replace(/[()]/g, '').replace(/,/g, '')
                            ) * (value.includes('(') ? -1 : 1);
                        }
                        return value || 0;
                    };
                    const n1 = beautifyNumber(v1);
                    const n2 = beautifyNumber(v2);
                    const num1 = parseNumber(n1);
                    const num2 = parseNumber(n2);
                    return num1 - num2; // Standard ascending sort
                },
                renderCell: (params) => {
                    // Collection balance — shown for ManageOnly / TaxRefund too (CRM + Archive clients).
                    const rawValue = params.value;
                    if (rawValue === '-' || rawValue === null || rawValue === undefined || rawValue === '') {
                        return <div>{beautifyNumber(0)}</div>;
                    }
                    const numeric = typeof rawValue === 'number' ? rawValue : Number(rawValue);
                    return <div>{beautifyNumber(Number.isFinite(numeric) ? numeric : 0)}</div>;
                },
            });
        }

        return baseColumns
            .filter((col) => !ViewSubAccountantClients || col.field !== 'withholdingReporting')
            .map((col) => ({
                ...col,
                viewTier: col.viewTier || COLUMN_VIEW_TIERS[col.field] || 'standard',
            }));
    }, [t, subAccountants, ViewSubAccountantClients, onWithholdingReportingToggle, withholdingReportingSavingCaseId]);

    const orderedColumnMeta = useMemo(
        () => orderColumns(baseColumns, columnOrder),
        [baseColumns, columnOrder]
    );

    const isColumnVisible = (col) => {
        if (tableView === 'custom') return columnVisibilityModel[col.field] !== false;
        const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
        return included.includes(col.viewTier);
    };

    const effectiveVisibilityModel = useMemo(() => {
        if (tableView === 'custom') return columnVisibilityModel;
        const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
        const model = {};
        baseColumns.forEach((col) => {
            if (!included.includes(col.viewTier)) model[col.field] = false;
        });
        return model;
    }, [tableView, columnVisibilityModel, baseColumns]);

    const handlePanelVisibilityChange = (field, checked) => {
        setColumnVisibilityModel((prev) => {
            if (tableView === 'custom') return { ...prev, [field]: checked };
            const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
            const next = {};
            baseColumns.forEach((col) => {
                next[col.field] = included.includes(col.viewTier);
            });
            next[field] = checked;
            return next;
        });
        if (tableView !== 'custom') {
            skipCustomLoadRef.current = true;
            onTableViewChange?.('custom');
        }
    };

    const reorderColumns = (fromField, toField) => {
        if (!fromField || !toField || fromField === toField) return;
        setColumnOrder((prev) => {
            const fields = orderColumns(baseColumns, prev).map((col) => col.field);
            const from = fields.indexOf(fromField);
            const to = fields.indexOf(toField);
            if (from < 0 || to < 0) return prev;
            const next = [...fields];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            try {
                localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_ORDER, JSON.stringify(next));
            } catch (_) {}
            return next;
        });
    };


    const apiRef = useGridApiRef();
    const gridWrapRef = useRef(null);
    const columnWidthsRef = useRef(columnWidths);
    const isResizingColumnRef = useRef(false);
    const prevFitRef = useRef(fitToScreen);
    const fitTurnedOnRef = useRef(false);
    const lastFitNonceRef = useRef(fitNonce);
    const [widthsCustom, setWidthsCustom] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY_MAIN_COLUMN_WIDTHS_CUSTOM) === '1'; } catch { return false; }
    });
    const [containerWidth, setContainerWidth] = useState(0);
    columnWidthsRef.current = columnWidths;
    if (fitToScreen && !prevFitRef.current) fitTurnedOnRef.current = true;
    prevFitRef.current = fitToScreen;

    const persistColumnWidths = (next) => {
        columnWidthsRef.current = next;
        setColumnWidths(next);
        try {
            localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_WIDTHS, JSON.stringify(next));
        } catch (_) {}
    };

    useLayoutEffect(() => {
        const el = gridWrapRef.current;
        if (!el) return undefined;
        const update = () => {
            if (isResizingColumnRef.current) return;
            setContainerWidth(Math.floor(el.clientWidth));
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const api = apiRef.current;
        if (!api?.subscribeEvent) return undefined;
        const onStart = api.subscribeEvent('columnResizeStart', () => {
            isResizingColumnRef.current = true;
        });
        const onStop = api.subscribeEvent('columnResizeStop', () => {
            isResizingColumnRef.current = false;
        });
        return () => {
            onStart();
            onStop();
        };
    }, [apiRef]);

    useLayoutEffect(() => {
        if (!fitToScreen || containerWidth <= 0 || isResizingColumnRef.current) return;
        const nonceChanged = lastFitNonceRef.current !== fitNonce;
        lastFitNonceRef.current = fitNonce;
        const turnedOn = fitTurnedOnRef.current || nonceChanged;
        fitTurnedOnRef.current = false;
        if (widthsCustom && !turnedOn) return;

        const fitted = computeFittedWidths(
            orderedColumnMeta,
            effectiveVisibilityModel,
            containerWidth,
            columnWidthsRef.current
        );
        if (!fitted) return;
        if (turnedOn) {
            setWidthsCustom(false);
            try { localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_WIDTHS_CUSTOM, '0'); } catch (_) {}
        }
        if (!widthsDiffer(columnWidthsRef.current, fitted, orderedColumnMeta, effectiveVisibilityModel)) return;
        persistColumnWidths({ ...columnWidthsRef.current, ...fitted });
    }, [fitToScreen, containerWidth, orderedColumnMeta, effectiveVisibilityModel, widthsCustom, fitNonce]);

    // Handlers for sorting and filtering
    const handleSortModelChange = (model) => {
        // Implement your sort logic here
    };

    const handleFilterModelChange = (model) => {
        // Implement your filter logic here
    };

    const handleEdit = (params) => {
        // Implement your edit logic here
    };

    const getLocale = () => {
        switch (i18n.language) {
            case 'ar':
                return arSA; // Arabic locale
            case 'he':
                return he; // Hebrew locale
            default:
                return enUS; // Default to English locale
        }
    };

    const columnsWithWidths = useMemo(() => {
        return orderedColumnMeta.map((col) => {
            const rest = { ...col };
            delete rest.viewTier;
            const w = columnWidths[rest.field];
            if (w != null && w > 0) {
                return { ...rest, width: w, minWidth: 40, flex: 0 };
            }
            return rest;
        });
    }, [orderedColumnMeta, columnWidths]);

    const handleColumnWidthChange = (params) => {
        const field = params.colDef.field;
        const width = params.width;
        if (!field || !(width > 0)) {
            isResizingColumnRef.current = false;
            return;
        }

        setWidthsCustom(true);
        try { localStorage.setItem(STORAGE_KEY_MAIN_COLUMN_WIDTHS_CUSTOM, '1'); } catch (_) {}
        persistColumnWidths({ ...columnWidthsRef.current, [field]: width });
        isResizingColumnRef.current = false;
    };

    return (
        // <Box
        //     sx={{
        //         height: '50vh',
        //         width: '100%',
        //         boxSizing: 'border-box !important',
        //         backgroundColor: '#fff',
        //         direction: 'ltr',
        //     }}
        // >
        <div className="client-main-table-container">
        <div ref={gridWrapRef} className={`client-main-table-inner ${direction}`}>
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={getLocale()}
            >
                <StyledDataGrid
                    key={`${tableView}-${fontSize}`}
                    className={fitToScreen ? 'fit-to-screen' : undefined}
                    apiRef={apiRef}
                    rows={filteredClients}
                    columns={columnsWithWidths}
                    columnVisibilityModel={effectiveVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    onColumnWidthChange={handleColumnWidthChange}
                    rowHeight={rowHeight}
                    columnHeaderHeight={rowHeight}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedRowIds}
                    onRowSelectionModelChange={(ids) => onSelectedRowIdsChange?.(ids)}
                    getRowClassName={(params) => {
                        if (params.row.leadingBusiness) return 'leading-business-row';
                        return params.row.rowClassName || '';
                    }}
                    getRowId={(row) => row.caseId}
                    onCellEditCommit={handleEdit}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                        row: {
                            className: !activeUsers ? 'disableUser' : '',
                        },
                    }}
                    pagination
                    onSortModelChange={handleSortModelChange}
                    onFilterModelChange={handleFilterModelChange}
                    onRowClick={(params) => viewClientDetails(params.row)}
                />
            </LocalizationProvider>
        </div>
        <Popover
            open={Boolean(columnsPanelAnchor)}
            anchorEl={columnsPanelAnchor}
            onClose={onColumnsPanelClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'right' : 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'right' : 'left' }}
            PaperProps={{
                sx: {
                    p: 0,
                    mt: 0.5,
                    maxHeight: 'min(70vh, 520px)',
                    width: 260,
                    minWidth: 260,
                    maxWidth: 260,
                    borderRadius: '8px',
                    border: '1px solid #e5e7f6',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    overflowY: 'auto',
                },
            }}
        >
            {orderedColumnMeta.map((col, index) => {
                const isChecked = isColumnVisible(col);
                const isDragging = dragField === col.field;
                return (
                    <Box
                        key={col.field}
                        dir={direction}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            const from = dragField || e.dataTransfer.getData('text/plain');
                            reorderColumns(from, col.field);
                            setDragField(null);
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            minHeight: 'var(--xtram-menu-item-height)',
                            opacity: isDragging ? 0.45 : 1,
                            backgroundColor: isChecked ? 'rgba(48, 79, 255, 0.1)' : 'transparent',
                            borderBottom: index < orderedColumnMeta.length - 1 ? '1px solid #f0f0f0' : 'none',
                            transition: 'background-color 0.2s ease, color 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#304fff',
                                '& .MuiFormControlLabel-label': { color: '#ffffff' },
                                '& .column-drag-handle': { color: '#ffffff' },
                            },
                            '&:hover .MuiCheckbox-root': { color: '#ffffff' },
                            '&:hover .MuiCheckbox-root.Mui-checked': { color: '#ffffff' },
                        }}
                    >
                        <Box
                            className="column-drag-handle"
                            draggable
                            onDragStart={(e) => {
                                setDragField(col.field);
                                e.dataTransfer.setData('text/plain', col.field);
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => setDragField(null)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'stretch',
                                px: 0.75,
                                cursor: 'grab',
                                color: '#8b93a7',
                                flexShrink: 0,
                            }}
                        >
                            <DragIndicatorIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <FormControlLabel
                            dir={direction}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={isChecked}
                                    onChange={(e) => handlePanelVisibilityChange(col.field, e.target.checked)}
                                    sx={{
                                        color: isChecked ? '#304FFF' : '#8b93a7',
                                        '&.Mui-checked': { color: '#304FFF' },
                                    }}
                                />
                            }
                            label={col.headerName}
                            sx={{
                                display: 'flex',
                                flex: 1,
                                minWidth: 0,
                                mx: 0,
                                py: 0.65,
                                height: 'auto',
                                cursor: 'pointer',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 'var(--xtram-text-sm)',
                                    fontWeight: isChecked ? 500 : 400,
                                    lineHeight: 1.35,
                                    color: isChecked ? '#304FFF' : '#696665',
                                },
                            }}
                        />
                    </Box>
                );
            })}
        </Popover>
        </div>
        // </Box>
    );
};

export default ClientsDataGrid;
